import React, { useEffect, useRef, useState } from "react";
import LeafletMap from "@/components/LeafletMap";

const GKEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

let gmPromise = null;
function loadGoogle() {
  if (window.google && window.google.maps) return Promise.resolve(window.google);
  if (gmPromise) return gmPromise;
  gmPromise = new Promise((resolve, reject) => {
    if (!GKEY) { reject(new Error("nokey")); return; }
    window.gm_authFailure = () => reject(new Error("auth"));
    const s = document.createElement("script");
    s.src = `https://maps.googleapis.com/maps/api/js?key=${GKEY}&v=3.54&language=it&region=IT`;
    s.async = true;
    s.onerror = () => reject(new Error("script"));
    s.onload = () => (window.google && window.google.maps ? resolve(window.google) : reject(new Error("nomaps")));
    document.head.appendChild(s);
  });
  return gmPromise;
}

// Interactive Google Map with automatic fallback to Leaflet/OSM if the
// Google Maps JavaScript API is unavailable (key missing or API not enabled).
export default function AppMap({ center, zoom = 14, markers = [], userMarker, draggable = false, onDragEnd, onMarkerClick, style, className = "" }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const gmarkersRef = useRef([]);
  const dragRef = useRef(null);
  const roRef = useRef(null);
  const [failed, setFailed] = useState(!GKEY);

  // init google map
  useEffect(() => {
    if (failed) return;
    let cancelled = false;
    loadGoogle()
      .then((google) => {
        if (cancelled || !containerRef.current) return;
        const map = new google.maps.Map(containerRef.current, {
          center: { lat: center.lat, lng: center.lng },
          zoom,
          disableDefaultUI: true,
          zoomControl: true,
          gestureHandling: "greedy",
          clickableIcons: false,
          renderingType: google.maps.RenderingType && google.maps.RenderingType.RASTER,
        });
        mapRef.current = map;
        drawUser(google);
        if (draggable) drawDrag(google);
        else drawMarkers(google);
        // Google renders grey tiles if the container was sized after init;
        // nudge it repeatedly + on any container resize until layout settles.
        const nudge = () => {
          if (!mapRef.current) return;
          google.maps.event.trigger(mapRef.current, "resize");
          mapRef.current.setCenter({ lat: center.lat, lng: center.lng });
        };
        [150, 400, 900, 1600, 2600].forEach((t) => setTimeout(nudge, t));
        try {
          const ro = new ResizeObserver(() => nudge());
          ro.observe(containerRef.current);
          roRef.current = ro;
        } catch (_) {}
      })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; if (roRef.current) { roRef.current.disconnect(); roRef.current = null; } };
    // eslint-disable-next-line
  }, [failed]);

  // recenter
  useEffect(() => {
    if (mapRef.current && center) mapRef.current.setCenter({ lat: center.lat, lng: center.lng });
    if (draggable && dragRef.current && center) dragRef.current.setPosition({ lat: center.lat, lng: center.lng });
    // eslint-disable-next-line
  }, [center?.lat, center?.lng]);

  function drawUser(google) {
    if (!userMarker || !mapRef.current) return;
    new google.maps.Marker({
      position: { lat: userMarker.lat, lng: userMarker.lng },
      map: mapRef.current,
      icon: { path: google.maps.SymbolPath.CIRCLE, scale: 7, fillColor: "#2563EB", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 3 },
      zIndex: 1,
    });
  }

  function drawDrag(google) {
    if (dragRef.current) dragRef.current.setMap(null);
    const m = new google.maps.Marker({
      position: { lat: center.lat, lng: center.lng },
      map: mapRef.current,
      draggable: true,
    });
    m.addListener("dragend", () => { const p = m.getPosition(); onDragEnd && onDragEnd(p.lat(), p.lng()); });
    dragRef.current = m;
  }

  function drawMarkers(google) {
    gmarkersRef.current.forEach((m) => m.setMap(null));
    gmarkersRef.current = [];
    markers.forEach((mk) => {
      if (mk.lat == null || mk.lng == null) return;
      const m = new google.maps.Marker({
        position: { lat: mk.lat, lng: mk.lng },
        map: mapRef.current,
        title: mk.title || mk.store_name || "",
        label: { text: String(mk.label || ""), color: "#E63946", fontWeight: "800", fontSize: "12px" },
      });
      if (onMarkerClick) m.addListener("click", () => onMarkerClick(mk));
      gmarkersRef.current.push(m);
    });
  }

  // redraw markers when they change
  useEffect(() => {
    if (failed || draggable || !mapRef.current || !window.google) return;
    drawMarkers(window.google);
    // eslint-disable-next-line
  }, [JSON.stringify(markers), failed]);

  if (failed) {
    return (
      <LeafletMap center={center} zoom={zoom} markers={markers} userMarker={userMarker}
        draggable={draggable} onDragEnd={onDragEnd} onMarkerClick={onMarkerClick} style={style} className={className} />
    );
  }

  return <div ref={containerRef} className={className} style={{ width: "100%", ...style }} data-testid="google-map" />;
}
